"use client"

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "~/lib/utils"

const tabsVariants = cva("h-pill-tabs", {
  variants: {
    size: {
      md: "h-pill-tabs--md",
    },
    fullWidth: {
      true: "h-pill-tabs--full",
    },
  },
})

const PillTabs = TabsPrimitive.Root

export interface PillTabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsVariants> {
  size?: "md"
  fullWidth?: boolean
}

const PillTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, size, fullWidth, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsVariants({ size, fullWidth, className }))}
    {...props}
  />
))
PillTabsList.displayName = TabsPrimitive.List.displayName

const PillTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn("h-pill-tabs__link", className)}
    {...props}
  />
))
PillTabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const PillTabsTriggerName = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <span ref={ref} className={cn("h-pill-tabs__name", className)} {...props} />
))
PillTabsTriggerName.displayName = "PillTabsTriggerName"

const PillTabsTriggerHint = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <span ref={ref} className={cn("h-pill-tabs__hint", className)} {...props} />
))
PillTabsTriggerHint.displayName = "PillTabsTriggerHint"

const PillTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} className={cn(className)} {...props} />
))
PillTabsContent.displayName = TabsPrimitive.Content.displayName

export {
  PillTabs,
  PillTabsList,
  PillTabsTriggerName,
  PillTabsTriggerHint,
  PillTabsTrigger,
  PillTabsContent,
}
